import type { CSSProperties } from 'react';
import { Box, Group, Stack, Text } from '@mantine/core';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';

import { IconFigmaCheckmarkS, IconFigmaClockS, IconFigmaErrorS } from '@/assets/icons';

/**
 * Figma "Status Tracker / Step" (node 4859:13767) — read-only prikaz položaja dokumenta u
 * statusnom tijeku (Ponuda, Ugovor, Radni paket, Tjedni izvještaj, Faktura, Aktivnost). NIJE
 * interaktivan (nema hover/klik) — akcije koje mijenjaju status idu isključivo kroz postojeće
 * gumbe/modalne prozore, ne kroz ovu komponentu.
 *
 * - done — prošli korak (zelena kvačica, zelen konektor do sljedećeg koraka)
 * - current — trenutni korak (bijeli krug s tamnoplavim prstenom i točkom, isto kao Figma "Dot" asset)
 * - upcoming — budući korak (prazan sivi krug)
 * - exception — izlazno stanje (Odbijen/Otkazan/Istekla...) — ZAMJENJUJE ostatak tijeka, ne
 *   dodaje se kao dodatan korak pored njega (vidi builder funkcije po entitetu, npr. offerDisplay.ts)
 * - paused — privremeno stanje (Na čekanju, Dospjela)
 */
export type StatusTrackerStepState = 'done' | 'current' | 'upcoming' | 'exception' | 'paused';

export interface StatusTrackerStepData {
  key: string;
  label: string;
  state: StatusTrackerStepState;
  meta?: string;
}

interface StatusTrackerProps {
  steps: StatusTrackerStepData[];
}

const DOT_SIZE = 20;

const dotStyle = (state: StatusTrackerStepState): CSSProperties => {
  switch (state) {
    case 'done':
      return {
        backgroundColor: 'var(--color-status-success-soft)',
        borderColor: 'var(--color-status-success)'
      };
    case 'exception':
      return {
        backgroundColor: 'var(--color-status-danger-soft)',
        borderColor: 'var(--color-status-danger)'
      };
    case 'paused':
      return {
        backgroundColor: 'var(--color-status-warning-soft)',
        borderColor: 'var(--color-status-warning)'
      };
    case 'current':
      // Bijeli krug s tamnoplavim prstenom — vidi StatusDot niže za unutarnju točku
      // (isto obilježje kao Figma "Dot" SVG asset: rect fill=white, stroke=#1B2A4A, inner circle #1B2A4A).
      return {
        backgroundColor: 'var(--color-bg-surface)',
        borderColor: 'var(--color-text-primary)'
      };
    case 'upcoming':
    default:
      return {
        backgroundColor: 'var(--color-bg-surface)',
        borderColor: 'var(--color-border-default)'
      };
  }
};

const connectorColor = (state: StatusTrackerStepState) =>
  state === 'done' ? 'var(--color-status-success)' : 'var(--color-border-default)';

const labelStyle = (state: StatusTrackerStepState): CSSProperties => {
  switch (state) {
    case 'exception':
      return { fontWeight: 600, color: 'var(--color-status-danger)' };
    case 'current':
    case 'paused':
      return { fontWeight: 600, color: 'var(--color-text-primary)' };
    case 'done':
      return { fontWeight: 400, color: 'var(--color-text-secondary)' };
    case 'upcoming':
    default:
      return { fontWeight: 400, color: 'var(--color-text-muted)' };
  }
};

const StatusDot = ({ state }: { state: StatusTrackerStepState }) => (
  <Box
    style={{
      position: 'relative',
      width: DOT_SIZE,
      height: DOT_SIZE,
      flexShrink: 0,
      borderRadius: DOT_SIZE / 2,
      borderWidth: 1.5,
      borderStyle: 'solid',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      ...dotStyle(state)
    }}
  >
    {state === 'done' && <IconFigmaCheckmarkS size={11} color="var(--color-status-success)" />}
    {state === 'exception' && <IconFigmaErrorS size={11} color="var(--color-status-danger)" />}
    {state === 'paused' && <IconFigmaClockS size={11} color="var(--color-status-warning)" />}
    {state === 'current' && (
      <Box
        style={{
          width: 8,
          height: 8,
          borderRadius: 4,
          backgroundColor: 'var(--color-text-primary)'
        }}
      />
    )}
  </Box>
);

const StatusDotsRow = ({ steps }: { steps: StatusTrackerStepData[] }) => (
  <Group gap={6} wrap="nowrap" style={{ width: '100%' }}>
    {steps.map((step, index) => (
      <Fragment key={step.key}>
        <StatusDot state={step.state} />
        {index < steps.length - 1 && (
          <Box style={{ flex: 1, height: 2, backgroundColor: connectorColor(step.state) }} />
        )}
      </Fragment>
    ))}
  </Group>
);

const StatusLabelsGrid = ({
  steps,
  compact = false
}: {
  steps: StatusTrackerStepData[];
  compact?: boolean;
}) => (
  <Box
    style={{
      display: 'grid',
      gridTemplateColumns: `repeat(${Math.max((steps.length - 1) * 2, 1)}, minmax(0, 1fr))`,
      paddingInline: DOT_SIZE / 2
    }}
  >
    {steps.map((step, index) => {
      const isFirst = index === 0;
      const isLast = index === steps.length - 1 && steps.length > 1;
      const align = isFirst ? 'left' : isLast ? 'right' : 'center';
      return (
        <Stack
          key={step.key}
          gap={2}
          px={4}
          style={{
            gridColumn: isFirst
              ? '1 / span 1'
              : isLast
                ? `${index * 2} / span 1`
                : `${index * 2} / span 2`,
            gridRow: 1,
            marginLeft: isFirst ? -DOT_SIZE / 2 - 4 : undefined,
            marginRight: isLast ? -DOT_SIZE / 2 - 4 : undefined,
            minWidth: 0
          }}
        >
          <Text
            fz={compact ? 11 : 13}
            lh={compact ? 1.25 : undefined}
            ta={align}
            style={labelStyle(step.state)}
          >
            {step.label}
          </Text>
          {!compact && step.meta && (
            <Text fz={11} ta={align} style={{ color: 'var(--color-text-muted)' }}>
              {step.meta}
            </Text>
          )}
        </Stack>
      );
    })}
  </Box>
);

const ACTIVE_STATES: StatusTrackerStepState[] = ['current', 'paused', 'exception'];

export const StatusTracker = ({ steps }: StatusTrackerProps) => {
  const { t } = useTranslation();

  const activeIndex = steps.findIndex((step) => ACTIVE_STATES.includes(step.state));
  const firstUpcoming = steps.findIndex((step) => step.state === 'upcoming');
  const currentIndex =
    activeIndex !== -1 ? activeIndex : firstUpcoming !== -1 ? firstUpcoming : steps.length - 1;

  return (
    <Box
      style={{
        position: 'relative',
        width: '100%',
        borderRadius: 12,
        border: '1px solid var(--color-border-default)',
        backgroundColor: 'var(--color-bg-surface)',
        padding: '16px 20px'
      }}
    >
      <Stack gap={10}>
        <Group justify="space-between" wrap="nowrap" gap={8}>
          <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}>
            {t('common.statusTrackerTitle')}
          </Text>
          <Text fz={11} hiddenFrom="sm" style={{ color: 'var(--color-text-muted)' }}>
            {t('common.statusTrackerStep', { current: currentIndex + 1, total: steps.length })}
          </Text>
        </Group>

        {/* Desktop — prva točka uz lijevi rub, zadnja uz desni; srednje labele centrirane ispod
            točke, prva poravnata lijevo, zadnja desno (da ne izlaze iz kartice). Labele su u gridu
            s polu-stupcima između točaka, pa se središte svake labele poklapa sa središtem točke. */}
        <Stack gap={4} visibleFrom="sm" style={{ width: '100%' }}>
          <StatusDotsRow steps={steps} />
          <StatusLabelsGrid steps={steps} />
        </Stack>

        {/* Mobile — točke vodoravno sa skraćenim nazivima koraka ispod. */}
        <Stack gap={4} hiddenFrom="sm" style={{ width: '100%' }}>
          <StatusDotsRow steps={steps} />
          <StatusLabelsGrid steps={steps} compact />
        </Stack>
      </Stack>
    </Box>
  );
};
